import React, { memo } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Typography from '../../atoms/typography';
import Ruler from '../../atoms/ruler';

const Block = styled.div`
  display: flex;
  border-top: 1px solid ${(props) => props.theme.color.brand};
  flex-direction: column;
  padding: ${(props) => props.theme.modularScale.large} 0 0;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    flex-direction: row;
  }
`;

const BlockTitle = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  flex: 0 0 auto;
  padding-right: ${(props) => props.theme.modularScale.large};
  width: 100%;

  a {
    display: inline;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    margin-bottom: 0;
    width: 25%;
  }
`;

const BlockContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    margin-top: 2rem;
  }

  > div:first-child {
    margin-top: 0;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    width: 75%;

  }
`;

const BlockContent = styled.div`
  flex: 0 0 auto;
  width: 100%;

  &:last-child {
    dt {
      margin-bottom: 0;
    }
  }

  p,
  ul,
  div {
    color: ${(props) => props.theme.color.brand};

    &:not(:last-child) {
      margin-bottom: 1.2em;
    }
  }

  ul {
    list-style-type: disc;
    list-style-position: inside;
  }
`;

const Dl = styled(Typography)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: ${(props) => props.theme.modularScale.medium} 0;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const Dt = styled.dt`
  flex: 0 0 auto;
  width: 100%;
  letter-spacing: 0.24em;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    width: 50%;
  }
`;

const Dd = styled.dd`
  color: ${(props) => props.theme.color.brand};
  flex: 0 0 auto;
  font-size: 13px;
  font-family: ${(props) => props.theme.font.sans};
  text-decoration: none;
  width: 100%;

  a {
    display: inline;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    width: 50%;
  }
`;

const SubHead = styled.span`
  color: ${(props) => props.theme.color.brand};
  display: block;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;

  a {
    display: inline;
    font-family: ${(props) => props.theme.font.caption};
    text-decoration: none;
    letter-spacing: 0.24em;
  }
`;

const SubHeadText = styled(Typography)`
  a {
    display: inline;
  }
`;

const SubHeadContent = styled.div`
  ${SubHead} {
    margin-bottom: 1em;
  }

  &:not(:last-child) {
    margin-bottom: ${(props) => props.theme.modularScale.xlarge};
  }
`;

const ParagraphContent = styled(Typography)`
  margin: ${(props) => props.theme.modularScale.small} 0;

  a {
    font-family: inherit;
    display: inline;
  }
`;

const DynamicInfo = (props) => {
  const generateRandomId = () => Math.floor(Date.now() / (Math.random() * 200));

  const generateContentMarkup = ({ content }) => {
    const items = [content];
    const itemsNum = items.length;
    const result = [];
    let currentContentTypeObj = { type: null, items: [] };

    // Splitting the current content's items array in many sub-arrays
    for (let i = 0; i < itemsNum; i++) {
      // If it's a new type of content
      // we push the `currentContentTypeObj` object
      // `result` and initialize a new one.
      if (currentContentTypeObj.type !== items[i].type && i > 0) {
        result.push(currentContentTypeObj);
        currentContentTypeObj = { type: null, items: [] };
      }
      // Setting the current content type
      currentContentTypeObj.type = items[i].type;

      // Adding the current content to `currentContentTypeObj.items`
      let key;
      switch (items[i].type) {
        case 'list':
          key = 'list';
          break;
        case 'paragraph':
          key = 'paragraph_text';
          break;
        default:
          break;
      }
      currentContentTypeObj.items.push(items[i][key]);
    }

    // Pushing the last `currentContentTypeObj` object to `result`
    result.push(currentContentTypeObj);

    const markup = (
      <>
        {
          // eslint-disable-next-line array-callback-return, consistent-return
          result.map((block) => {
            switch (block.type) {
              case 'paragraph':
              {
                return block.items.map((item) => (
                  <ParagraphContent key={generateRandomId()} element="p" like="paragraph-2" dangerouslySetInnerHTML={{ __html: item }} />
                ));
              }

              case 'list':
              {
                return (
                  <ul>
                    {
                      block.items.map((item) => (
                        <li key={generateRandomId()}>{item}</li>
                      ))
                    }
                  </ul>
                );
              }

              default:
                break;
            }
          })
        }
      </>
    );

    return markup;
  };

  return (
    <>
      <Block key={props.data.heading}>
        {props.data?.heading && (
          <BlockTitle
            element="h2"
            like="heading-5"
            dangerouslySetInnerHTML={{ __html: props.data.heading }}
          />
        )}

        <BlockContentWrapper>
          {props.data?.elements?.length > 0
            && props.data.elements
              .map((element) => (
                <BlockContent key={element.id}>
                  {element.type === 'definition' && (
                  <Dl element="dl" like="label-1" key={element.term}>
                    <Dt>
                      <SubHead
                        element="span"
                        like="dec-1"
                        dangerouslySetInnerHTML={{ __html: element.term }}
                      />
                    </Dt>
                    <Dd dangerouslySetInnerHTML={{ __html: element.definition }} />
                  </Dl>
                  )}

                  {element.type === 'subhead' && (
                  <Dl element="dl" like="label-1">
                    <SubHeadContent key={element.subhead}>
                      {element.subhead && (
                        <SubHead>{element.subhead}</SubHead>
                      )}
                      <SubHeadText
                        element="p"
                        like="paragraph-2"
                        dangerouslySetInnerHTML={{ __html: element.subhead_text }}
                      />
                    </SubHeadContent>
                  </Dl>
                  )}

                  {(element.type === 'paragraph' || element.type === 'list') && generateContentMarkup({ content: element })}
                </BlockContent>
              ))}
        </BlockContentWrapper>
      </Block>

      {props.data?.hr && <Ruler />}
    </>
  );
};

DynamicInfo.propTypes = {
  data: PropTypes.object.isRequired
};

DynamicInfo.whyDidYouRender = true;

export default memo(DynamicInfo);
